import * as z from 'zod';
import { isOrderWindowWithinHours } from '@noor/eats-api';
import { venueHoursSchema } from '@/entities/venue';

/** Translator shape (i18next's `t`), passed in so validation messages localize. */
type Translate = (key: string) => string;

/**
 * Payload for the edit-working-hours modal, validated at the modal registry
 * boundary. The current weekly schedule seeds the form's field array.
 *
 * `branchId` names the branch being edited. It is optional so the
 * un-parameterized `/eats/venue` route can keep opening the modal for the
 * session's active branch, but the by-id venue screen always passes it — the
 * branches list can open ANY branch, and falling back to the active one there
 * would save one branch's hours onto another.
 */
export const editVenueHoursPayloadSchema = z.object({
  workingHours: z.array(venueHoursSchema),
  branchId: z.string().optional(),
});
export type EditVenueHoursPayload = z.infer<typeof editVenueHoursPayloadSchema>;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Build the working-hours form schema with localized messages. Each day is a
 * `{ label, isOpen, open, close }` row (a `useFieldArray` entry). Time fields
 * use wall-clock times, including overnight hours and a 24:00 closing time.
 * An order window must fit inside this day's working interval.
 */
export function buildVenueHoursFormSchema(t: Translate) {
  const time = z.string().regex(HHMM, t('venue.hoursForm.timeInvalid'));
  return z.object({
    days: z
      .array(
        z
          .object({
            label: z.string(),
            isOpen: z.boolean(),
            open: time,
            close: z.union([time, z.literal('24:00')]),
            orderFrom: z.string().optional(),
            orderTo: z.string().optional(),
          })
          .superRefine((day, ctx) => {
            if (
              day.isOpen &&
              !isOrderWindowWithinHours(day.open, day.close, day.orderFrom, day.orderTo)
            )
              ctx.addIssue({
                code: 'custom',
                path: [day.orderFrom ? 'orderTo' : 'orderFrom'],
                message: t('branches.form.orderWindowInvalid'),
              });
          }),
      )
      .min(1),
  });
}

export type VenueHoursFormValues = z.infer<ReturnType<typeof buildVenueHoursFormSchema>>;
